import { CalendarX } from 'lucide-react'

import { RADIUS } from '@/lib/surface'
import { cn } from '@/lib/utils'

/**
 * A level that cannot be started, in place of the button that would sell it.
 *
 * The shop's standing rule (owner's, and it applies site-wide): we sell only
 * what is on the calendar. A tier nobody has declared a day for — every graphic
 * sold out their Creator slots and none of them has filled in next month yet —
 * has no buy button at all, rather than a button that leads to a basket the
 * checkout will refuse.
 *
 * The card around it keeps its price and its feature list on purpose. The tier
 * still exists and is still worth reading; what is missing is a date, not the
 * product. So this reads as "not yet", never as "gone".
 *
 * It disappears on its own. `readyFrom` is read from the capacity calendar on
 * every request, so the moment a performer adds a week to their grid the button
 * is back — nobody deploys anything and nobody flips a flag.
 *
 * Deliberately NOT a `<button disabled>`: there is no action being withheld,
 * and a disabled control invites the reader to look for the thing that would
 * enable it. It is a statement.
 *
 * ── THE CHIP ONLY ────────────────────────────────────────────────────────
 * It used to carry two lines of explanation as well — "Ten poziom jest
 * chwilowo zapełniony. Napisz do nas…" — inside the card. On `/pl/montaz`,
 * where all three levels are currently full, that printed the same sentence
 * three times in a row across the bottom of the grid: six lines of identical
 * grey type, the largest single block of text on the page, saying one thing.
 * The sentence moved under the grid, where a page prints it ONCE however many
 * of its levels are full — and where it is now a control rather than a
 * sentence: `RestockAlertForm` takes an address and writes to it when the level
 * comes back.
 */
export function PlanSoldOut({ className }: { readonly className?: string }) {
  return (
    <span
      className={cn(
        // The same rhythm the live button keeps — `lib/tier-fit.ts` sets these
        // on the card, and a chip standing in for a button has to give way
        // with it. Hardcoded `mt-5 py-3` here cost the ladder 12px on a short
        // screen, which is exactly what a page of sold-out levels —
        // three of these chips — could not afford.
        'mt-[var(--tier-step,1.25rem)] inline-flex w-full items-center justify-center gap-2 px-5 py-[max(0.6rem,var(--tier-tick,0.75rem))]',
        RADIUS.pill,
        'bg-white/[0.06] text-[0.85rem] font-semibold text-white/45',
        className,
      )}
    >
      {/* The icon stays a struck-through calendar even though the word no
          longer mentions dates: what is actually missing IS a day in the
          capacity board, and the icon is the only thing on the chip that still
          says so. */}
      <CalendarX size={14} strokeWidth={2} absoluteStrokeWidth aria-hidden="true" />
      Chwilowo wyprzedane
    </span>
  )
}
